import { useState, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";

const navItems = [
  { to: "/", label: "Home", end: true },
  { to: "/about", label: "About" },
  { to: "/statements", label: "Statements" },
  { to: "/press", label: "Press" },
  { to: "/photos", label: "Photos" },
  { to: "/archive", label: "Archive" },
  { to: "/contact", label: "Contact" },
];

/**
 * Floating hamburger menu.
 * - Mobile (< 1024px): premium full-screen editorial overlay.
 * - Desktop (>= 1024px): constrained side drawer (max 320px) sliding in from
 *   the right over a dimmed backdrop, with an in-panel close button.
 */
export const FloatingMenu = () => {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!open) {
      document.body.style.overflow = "";
      return;
    }
    document.body.style.overflow = "hidden";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open]);

  return (
    <>
      <button
        className={`menu-fab ${open ? "menu-fab--hidden-desktop" : ""}`}
        aria-label={open ? "Close menu" : "Open menu"}
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        data-testid="menu-toggle"
      >
        <div className="flex flex-col items-center justify-center">
          <span className={`burger-line ${open ? "open" : ""}`} />
          <span className={`burger-line ${open ? "open" : ""}`} />
          <span className={`burger-line ${open ? "open" : ""}`} />
        </div>
      </button>

      <div
        className={`menu-overlay ${open ? "is-open" : ""}`}
        data-testid="menu-overlay"
        aria-hidden={!open}
      >
        {/* Full-screen dimmed backdrop — click to close */}
        <div className="menu-backdrop" onClick={() => setOpen(false)} />
        <div className="menu-aurora" aria-hidden="true" />
        <div className="menu-grain" aria-hidden="true" />

        {/* Panel — full-screen on mobile, side drawer on desktop */}
        <div className="menu-panel" data-testid="menu-panel">
          {/* In-panel close button (visible on desktop drawer) */}
          <button
            type="button"
            className="menu-close"
            aria-label="Close menu"
            onClick={() => setOpen(false)}
            data-testid="menu-close"
          >
            ×
          </button>

          {/* Decorative gold rule */}
          <div className="menu-rule" aria-hidden="true" />

          {/* Top eyebrow */}
          <div className="menu-eyebrow">
            <span className="menu-eyebrow-dot" />
            <span>Menu</span>
          </div>

          {/* Nav list */}
          <nav className="menu-nav" aria-label="Primary">
            <ul className="menu-list">
              {navItems.map((item, i) => (
                <li
                  key={item.to}
                  className="menu-item"
                  style={{ "--d": `${120 + i * 90}ms` }}
                >
                  <NavLink
                    to={item.to}
                    end={item.end}
                    onClick={() => setOpen(false)}
                    data-testid={`menu-link-${item.label.toLowerCase()}`}
                    className={({ isActive }) =>
                      `menu-link ${isActive ? "is-active" : ""}`
                    }
                  >
                    <span className="menu-num">{String(i + 1).padStart(2, "0")}</span>
                    <span className="menu-label">{item.label}</span>
                    <span className="menu-arrow" aria-hidden="true">→</span>
                  </NavLink>
                </li>
              ))}
            </ul>
          </nav>

          {/* Bottom meta */}
          <div className="menu-meta">
            <span className="menu-meta-line">Vineyard City Council</span>
            <span className="menu-meta-dot">·</span>
            <span className="menu-meta-line">Term 2026 – 2029</span>
          </div>
        </div>
      </div>
    </>
  );
};
